import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {DiversityMetrics} from '../../client-api.service';
import {TranslateService} from '@ngx-translate/core';
import {DiversityColors} from '../../../../model';
import {Color} from 'ng2-charts';
import {ChartDataSets} from 'chart.js';

@Component({
  selector: 'diversity-detail-view',
  templateUrl: './diversity-detail-view.component.html',
  styleUrls: ['./diversity-detail-view.component.scss']
})
export class DiversityDetailViewComponent implements OnInit {

  private MAX_LABEL_LENGTH = 38;
  @Input()
  public selectedFunnelStage: string;
  @Input()
  public metrics: DiversityMetrics;
  public barChartColors: Color[] = [];
  public genderData: number[] = [];
  public ethnicityData: number[] = [];
  public neurodiversityData: number[] = [];
  public disabilityData: number[] = [];
  public veteranData: number[] = [];
  public lgbtqiaData: number[] = [];

  public genderColors: Color[] = [];
  public ethnicityColors: Color[] = [];
  public neurodiversityColors: Color[] = [];
  public disabilityColors: Color[] = [];
  public veteranColors: Color[] = [];
  public lgbtqiaColors: Color[] = [];

  @Output()
  candidateCount = new EventEmitter<number>();
  private isInitialized: boolean = false;
  private isFirstChange: boolean = false;

  constructor(private translate: TranslateService) { }

  ngOnInit() {
    this.barChartColors =  [
      { backgroundColor: [
        DiversityColors.orange,
        DiversityColors.blue,
        DiversityColors.green,
        DiversityColors.purple,
        DiversityColors.brown,
        DiversityColors.blueGray,
        DiversityColors.red,
        DiversityColors.black,
        DiversityColors.gray] }
    ];
    this.populateData();

    this.genderColors = this.selectColors(this.genderData, false);
    this.ethnicityColors = this.selectColors(this.ethnicityData, false);
    this.neurodiversityColors = this.selectColors(this.neurodiversityData, false);
    this.disabilityColors = this.selectColors(this.disabilityData, false);
    this.veteranColors = this.selectColors(this.veteranData, true);
    this.lgbtqiaColors = this.selectColors(this.lgbtqiaData, false);

    this.isInitialized = true;
  }


  /**
   * The first change happens *before* init, so we want to ignore it.
   * We also want to ignore the first change *after* init, since it causes
   * the chart to render twice in quick succession.
   *
   * All subsequent changes should be processed.
   */
  ngOnChanges(changes: SimpleChanges) {

    if (!this.isInitialized) {
      return;
    }

    if (this.isFirstChange) {
      this.isFirstChange = false;
      return;
    }

    this.metrics = changes.metrics.currentValue;
    this.populateData();
  }

  populateData() {
    let metricsBase = null;
    switch (this.selectedFunnelStage) {
      case 'Applied':
        metricsBase = this.metrics.applied;
        break;
      case 'Submitted':
        metricsBase = this.metrics.submitted;
        break;
      case 'Rejected':
        metricsBase = this.metrics.rejected;
        break;
      case 'Interviewed':
        metricsBase = this.metrics.interviewed;
        break;
      case 'Offer Extended':
        metricsBase = this.metrics.offerExtended;
        break;
      case 'Hired':
        metricsBase = this.metrics.accepted;
        break;
      default:
        metricsBase = this.metrics.applied;
        console.error('Funnel Stage Selected didn\'t match any appropriate stages');
        break;
    }

    const stageCount = metricsBase.stageCount;
    this.candidateCount.emit(metricsBase.stageCount);

    this.genderData = [
      parseFloat((metricsBase.genderMale / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.genderFemale / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.genderNonBinary / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.genderNotSet / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.genderNotDisclosed / stageCount * 100).toFixed(2))];

    this.ethnicityData = [
      parseFloat((metricsBase.ethnicityAmericanIndianOrAlaskanNative / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.ethnicityAsian / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.ethnicityNativeHawaiianOrOtherPacificIslander / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.ethnicityHispanicOrLatino / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.ethnicityWhite / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.ethnicityBlackOrAfricanAmerican / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.ethnicityTwoOrMoreRaces / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.ethnicityUnknown / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.ethnicityNotDisclosed / stageCount * 100).toFixed(2))];

    this.neurodiversityData = [
      parseFloat((metricsBase.neurodivergentYes / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.neurodivergentNo / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.neurodivergentUnknown / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.neurodivergentNotDisclosed / stageCount * 100).toFixed(2))
    ];

    this.disabilityData = [
      parseFloat((metricsBase.disabledYes / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.disabledNo / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.disabledUnknown / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.disabledNotDisclosed / stageCount * 100).toFixed(2))
    ];

    this.veteranData = [
      parseFloat((metricsBase.veteranSpecialDisabled / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.veteranVietnam / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.veteranNewlySeparated / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.veteranOtherProtected / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.veteranNotAVeteran / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.veteranNotDisclosed / stageCount * 100).toFixed(2))
    ];

    this.lgbtqiaData = [
      parseFloat((metricsBase.lgbtqMember / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.lgbtqNonMember / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.lgbtqNotSpecified / stageCount * 100).toFixed(2)),
      parseFloat((metricsBase.lgbtqPreferNotToSay / stageCount * 100).toFixed(2))
    ];
  }

  reformatLabels(array) {
    const maxLength = this.MAX_LABEL_LENGTH;
    const translator = this.translate;
    array.forEach((label, index) => {
      const labelText = translator.instant(label);
      array[index] = labelText + (' '.repeat(maxLength - labelText.length));
    });
    return array;
  }

  selectColors(data, isVeteran) {
    const colorLength = isVeteran ? data.length - 1 : data.length - 2;
    const colorArray = this.barChartColors[0].backgroundColor;
    // This is ignored because ts-lint doesn't understand it will always be an array instead of a single string
    // @ts-ignore
    const selectedColors: string[] = colorArray.slice(0, colorLength);
    // -2 will always be Unknown in data and colors
    if (!isVeteran && data[data.length - 2] > 0) {
      selectedColors.push(colorArray[colorArray.length - 2]);
    }
    // -1 will always be Not Disclosed in data and colors
    if (data[data.length - 1] > 0) {
      selectedColors.push(colorArray[colorArray.length - 1]);
    }
    return [ { backgroundColor: selectedColors } ];
  }

}
